import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

import mapDataWorld from './world-continents';
import {getPercent} from '@ey/utils';
import Utility from '../../../../../util/utilityfunctions';
import {labels} from '../../../../../util/utils';
import {
	countries,
	reportingFilterId,
	dashboardTabId,
	completeUpperCase,
	dashboardChartColors
} from '../../../../../util/uiconstants';

import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../actions/Metrics/dashboardFiltersAction';
import normalizeDashboardFilters from '../../../../../util/dashboard/normalizeDashboardFilters';

import OutputText from '@ey/canvascoreservices/OutputText';

import Tile from '../../../../Common/Tile';
import Legend from '../../../../Common/Charts/Legend';
import StyledMapChart from './StyledMapChart';

// load Highcharts modules
require('highcharts/modules/map')(Highcharts);
const ReportCompletionStatus = (props) => {
	const {engagementMetrics} = props;
	const [data, setData] = useState([]);
	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);
	const [engagementsByCountry, setEngagementByCountry] = useState({});
	const dispatch = useDispatch();

	useEffect(() => {
		loadData();
	}, [engagementMetrics]);

	const loadData = () => {
		const engagementsWithCountryCode = engagementMetrics.map((eng) => {
			return {
				...eng,
				countryCode: countries.find((country) => {
					return country.name === eng.countryName;
				})?.code
			};
		});
		const groupMetrics = _.groupBy(engagementsWithCountryCode, 'countryCode');

		setEngagementByCountry({...groupMetrics});

		const metrics = Object.keys(groupMetrics).map((key) => {
			const [engCount, totalCompleted, totalTasks] = getEngagementTaskCompletion(groupMetrics[key]);

			return {
				code: key,
				value: totalTasks > 0 ? getPercent(totalCompleted, totalTasks) : 0,
				totalCompleted: totalCompleted,
				totalTasks: totalTasks,
				engCount: engCount
			};
		});

		setData(metrics);
	};

	const getEngagementTaskCompletion = (group) => {
		const totalCompleted = group.reduce((current, eng) => {
			return current + eng.allTC;
		}, 0);

		const total = group.reduce((current, eng) => {
			return current + eng.allTTot;
		}, 0);

		return [group.length, totalCompleted, total];
	};

	const chartRanges = [
		{
			to: -1,
			name: labels.notApplicable,
			color: dashboardChartColors.blue[0]
		},
		{
			from: 0,
			to: 0,
			name: '0%',
			color: dashboardChartColors.blue[1]
		},
		{
			from: 1,
			to: 49,
			name: '1%-49%',
			color: dashboardChartColors.blue[2]
		},
		{
			from: 50,
			to: 79,
			name: '50%-79%',
			color: dashboardChartColors.blue[3]
		},
		{
			from: 80,
			name: '>80%',
			color: dashboardChartColors.blue[4]
		}
	];

	const options = {
		chart: {
			map: mapDataWorld
		},
		title: {
			text: '',
			enabled: false
		},
		subtitle: {
			text: '',
			enabled: false
		},
		credits: {
			enabled: false
		},
		mapNavigation: {
			enabled: true,
			buttonOptions: {
				verticalAlign: 'top'
			},
			enableMouseWheelZoom: false
		},
		colorAxis: {dataClasses: chartRanges},
		legend: {
			enabled: false
		},
		tooltip: {
			useHTML: true,
			enabled: true,
			outside: true,
			followPointer: true,
			useHTML: true,
			formatter() {
				return `
					<div class="bar-tooltip" style="width: 20rem; white-space: initial">
						<strong>${this.point.name}</strong><br/>
						${Utility.getFormattedNumber(numberFormat, this.point.engCount)} ${
					this.point.engCount === 1 ? labels.engagement : labels.engagements
				}<br/>
						${Utility.getFormattedNumber(numberFormat, this.point.value)}% (${Utility.getFormattedNumber(
					numberFormat,
					this.point.totalCompleted
				)}/${Utility.getFormattedNumber(numberFormat, this.point.totalTasks)}) ${labels.completedTasks}
					</div>
				`;
			}
		},
		plotOptions: {
			series: {
				point: {
					events: {
						click: (e) => {
							onGraphicClickHandler(e);
						}
					}
				}
			}
		},
		series: [
			{
				data: data,
				joinBy: ['iso-a2', 'code'],
				states: {
					hover: {
						color: 'var(--green-200)'
					}
				},
				dataLabels: {
					enabled: false
				}
			}
		]
	};

	const handlerOnClearFilter = () => {
		dispatch(clearFilterDashboardTiles(dashboardTabId.reporting));
	};

	const onGraphicClickHandler = (highChartEvent) => {
		const {code: countryCode, name: countryName, value: percent} = highChartEvent.point;

		const filteredEngagementIds = engagementsByCountry[countryCode]?.map((eng) => eng.engId) || [];
		const rangeLabel = chartRanges.find((range) => range.from <= percent && range.to >= percent)?.name;
		const dashboardFilters = normalizeDashboardFilters(
			[
				{
					serieLabel: `${rangeLabel} ${labels.completeUpperCase}`,
					categoryLabel: countryName,
					type: reportingFilterId.completionByLocation,
					onClearChip: handlerOnClearFilter
				}
			],
			filteredEngagementIds,
			handlerOnClearFilter
		);

		dispatch(updateFilterDashboardTiles(dashboardTabId.reporting, dashboardFilters));
	};

	const renderLegend = ({name}) => (
		<OutputText body3 light className="grey">
			{name}
		</OutputText>
	);

	const tileProps = {
		title: labels.taskCompletionByLocation,
		subTitle: labels.basedOnCompletedTasksForEachEngagement,
		isEmpty: data.length === 0,
		emptyTitle: labels.thereIsNoDataToDisplay,
		emptySubtitle: labels.selectadditionalEntitiesToContinue,
		className: 'full'
	};

	const chartProps = {
		options: options,
		constructorType: 'mapChart',
		highcharts: Highcharts
	};

	return (
		<Tile {...tileProps}>
			<StyledMapChart>
				<HighchartsReact {...chartProps} />
				<Legend items={chartRanges} renderItem={renderLegend} title={labels.completionRange} />
			</StyledMapChart>
		</Tile>
	);
};

ReportCompletionStatus.defaultProps = {
	engagementMetrics: [],
	onGraphicClickHandler: () => {}
};

ReportCompletionStatus.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	),
	onGraphicClick: PropTypes.func
};

export default ReportCompletionStatus;
